import Component from "../../lib/component";
import {CsDragHandle} from "./cs_drag_handle";

export const CsBubbleConfig = {
    componentTypeKey: 'CsBubble',
    baseHtmlClass: 'cs_bubble',
    selector: '.cs_bubble',

    datasetConfigKey: 'cs_bubble__config',

    titleSelectorDatasetConfigKey: 'title_element_selector',
    isDraggableDatasetConfigKey: 'is_draggable',
}

export class CsBubble extends Component {

    static get config() {
        return CsBubbleConfig;
    }

    constructor(id_or_element) {
        super({
            element: id_or_element,
            config: CsBubbleConfig
        });

        this.setDatasetConfig(CsBubbleConfig.datasetConfigKey);
        this.isDraggable = this.getIsDraggableConfig();

        if (this.isDraggable) {
            this.parentElement = this.element.offsetParent;

            this.dragHandle = CsDragHandle.createDragHandle(this);

            this.pointerIn = false; // Will be true when the mouse is over the element.
            this.element.addEventListener('pointerenter', this.handlePointerEnter.bind(this));
            this.element.addEventListener('pointerleave', this.handlePointerLeave.bind(this));

            this.dragging = false; // Will be true during a drag.

            // It's necessary to store references to these handlers because `bind()` returns a new function reference. And we need that reference later to remove the listener.
            this.pointerMoveHandler = this.handlePointerMove.bind(this);
            this.pointerUpHandler = this.handlePointerUp.bind(this);
            this.element.addEventListener('pointerdown', this.handlePointerDown.bind(this));
        }

        this.firstView = false; // Is this the first view of the element?
        this.viewedCount = 0; // How many times has it intersected the viewport?
        this.inView = false; // Is the element within the viewport?
        this.viewedObserver = null;
        this.initViewedObserver();
        this.observeViewed();

        // Hold the bubble hidden across the gap between revealing it and the pop
        // animation taking over. The animation is applied by --first_view, which only
        // lands when the IntersectionObserver reports back — asynchronously — so
        // revealing here paints one fully opaque frame before its `backwards` fill
        // snaps the element to the 0% keyframe and hides it again for the animation's
        // delay. Without this the bubble flashes, disappears, then pops in.
        //
        // Only when an observer exists: with no IntersectionObserver nothing ever runs
        // the animation, so the bubble must reveal immediately rather than stay hidden.
        if (this.viewedObserver) this.addHtmlClassState('pending');

        this.element.style.display = null;
        this.render();
    }

    getIsDraggableConfig() {
        let config = this.getDatasetConfigValue(CsBubble.config.isDraggableDatasetConfigKey);
        if (typeof config === 'undefined' || config === null) return true;
        return config;
    }

    handlePointerEnter(event) {
        if (event.pointerType === 'mouse') this.pointerIn = true; // Only mouse events will set the pointerIn state to true.
        this.render();
    }

    handlePointerLeave(event) {
        if (event.pointerType === 'mouse') this.pointerIn = false;
        this.render();
    }

    // Dragging is handled by storing the starting position of the pointer and element on a "pointer down" event.
    // Then during the "pointer move" (dragging) we get the delta in the pointer from the staring position.
    // That delta is then applied to the elements starting position.
    handlePointerDown(event) {
        this.pointerIn = true;
        this.dragging = true;

        // Store the starting position of the pointer within the viewport:
        this.viewportStartX = event.clientX;
        this.viewportStartY = event.clientY;

        // Store the starting top and left position of the element:
        const position = this.getElementPosition();
        this.elementStartLeft = position.left;
        this.elementStartTop = position.top;

        // Set the element top and left.
        this.elementLeft = this.elementStartLeft;
        this.elementTop = this.elementStartTop;

        // Add listeners for the "drag" that started with this mousedown event.
        // These listeners are removed at the end of the "drag" (see `handleMouseUp()`).
        document.addEventListener('pointermove', this.pointerMoveHandler);
        document.addEventListener('pointerup', this.pointerUpHandler);

        this.render();
    }

    handlePointerMove(event) {
        // Calculate the change in position of the mouse pointer since the beginning of the drag (pointer down event):
        const deltaX = this.getPointerDeltaX(event.clientX);
        const deltaY = this.getPointerDeltaY(event.clientY);

        // Calc the element's new position by applying the change in position
        this.elementLeft = (this.elementStartLeft - deltaX);
        this.elementTop = (this.elementStartTop - deltaY);

        this.render();
    }

    handlePointerUp(event) {
        document.removeEventListener('pointermove', this.pointerMoveHandler);
        document.removeEventListener('pointerup', this.pointerUpHandler);

        // Update the element's new position to the final position of the drag.
        this.elementLeft = Math.round(this.elementLeft);
        this.elementTop = Math.round(this.elementTop);

        this.pointerIn = false;
        this.dragging = false;

        this.render();
    }

    getElementPosition() {
        const style = window.getComputedStyle(this.element);
        let top = style['top'].replace('px', '');
        if (top.length < 1) top = 0;
        let left = style['left'].replace('px', '');
        if (left.length < 1) left = 0;
        return {
            top: Math.round(parseFloat(top)),
            left: Math.round(parseFloat(left)),
        };
    }

    getPointerDeltaX(currentX) {
        return Math.round(this.viewportStartX - currentX)
    }

    getPointerDeltaY(currentY) {
        return Math.round(this.viewportStartY - currentY)
    }

    renderForFirstView() {
        // Hand off from --pending to the animation in the SAME task, so the two class
        // changes land in one style recalculation and no opaque frame is painted between.
        this.removeHtmlClassState('pending');
        this.addHtmlClassState(`first_view`);

        // HACk! - We need to remove the 'first_view' class after the animation has run which should be within 5 seconds.
        // Might be better to handle the full animation in JS and use the callback on animation completion.
        window.setTimeout(() => {
            this.removeHtmlClassState('first_view');
        }, 5000);

        this.firstView = false;
    }

    render() {
        this.conditionallySetHtmlClassState(this.isViewed(), 'viewed');
        this.conditionallySetHtmlClassState(this.isInView(), 'in_view');
        this.conditionallySetHtmlClassState(this.isDraggable, 'draggable');

        if (this.firstView) this.renderForFirstView();

        if (this.isDraggable) {
            this.conditionallySetHtmlClassState(this.pointerIn, 'pointer_in');
            this.conditionallySetHtmlClassState(this.dragging, 'dragging');
            // Update the element's new position…
            this.renderElementLeft(this.elementLeft);
            this.renderElementTop(this.elementTop);
        }
    }

    renderElementLeft(left) {
        if (typeof left === 'undefined' || left === null) return;
        this.element.style.left = `${left}px`;
    }

    renderElementTop(top) {
        if (typeof top === 'undefined' || top === null) return;
        this.element.style.top = `${top}px`;
    }


    /**
     * Set up an intersection observer for when the element is viewed within its scrollable parent element.
     * @return {undefined}
     */
    initViewedObserver() {
        if (!IntersectionObserver) return;
        let options = {
            rootMargin: '50px',
            threshold: 0.0,
        };
        this.viewedObserver = new IntersectionObserver(this.handleViewed.bind(this), options);
    }

    observeViewed() {
        if (!this.viewedObserver) return;
        this.viewedObserver.observe(this.element);
    }

    /**
     * Callback for when the viewedObserver detects the component intersecting the parent scroll element.
     * @param entries {IntersectionObserverEntry}
     * @param observer {IntersectionObserver}
     */
    handleViewed(entries, observer) {

        entries.forEach((entry) => {
            if (entry) {
                if (entry.isIntersecting) {
                    this.viewedCount += 1;
                    this.firstView = (this.viewedCount === 1);
                }
                this.inView = entry.isIntersecting;
            }
        });

        this.render();
    }

    isViewed() {
        return this.viewedCount > 0;
    }

    isInView() {
        return !!this.inView;
    }

}
